import { useMemo } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilCallback, useRecoilValue } from "recoil";

import type { IColumnsSizingStore } from "@ryde/controls";
import { useReportId } from "@ryde/redux";
import {
  companyIdSelector,
  reportsTableColumnsSizingAF,
  setReportColumnsSizingLocalStore,
  type ColumnSizingState,
} from "@ryde/shared";

export const useReportColumnsSizingStore = (): IColumnsSizingStore => {
  const { reportId } = useReportId();
  const companyId = useRecoilValue(companyIdSelector);

  const getColumnsSizing = useRecoilCallback(
    ({ snapshot }) =>
      (): ColumnSizingState =>
        snapshot.getLoadable(reportsTableColumnsSizingAF(reportId)).getValue(),
    [reportId],
  );

  const setColumnsSizing = useRecoilCallback(
    ({ set }) =>
      (columnsSizing: ColumnSizingState): void => {
        set(reportsTableColumnsSizingAF(reportId), columnsSizing);
      },
    [reportId],
  );

  // Report widths stay in this browser only: saving a report does not carry them.
  const commitColumnsSizing = useEvent(
    (columnsSizing: ColumnSizingState): void => {
      setColumnsSizing(columnsSizing);

      if (companyId) {
        setReportColumnsSizingLocalStore({
          companyId,
          reportId,
          columnsSizing,
        });
      }
    },
  );

  return useMemo(
    () => ({ getColumnsSizing, setColumnsSizing, commitColumnsSizing }),
    [commitColumnsSizing, getColumnsSizing, setColumnsSizing],
  );
};
